import { cva } from "class-variance-authority";

import { cn } from "./cn";

/*
 * @nt/ui/recipes — the look of every stateful primitive, as class strings.
 *
 * Each app keeps its own headless library (the customer app Base UI and Radix,
 * the console Base UI) and its own variant NAMES; a primitive there takes its
 * classes from here, so the two apps wear one system. Values come from the
 * public site (apps/web mk primitives, mk.css, company.css `.ct-field`) and
 * the product roles in @nt/tokens/theme.css — never a raw ramp, never a hex.
 *
 * Sizes follow the density tier: `h-control` is 40px, 36px inside
 * `[data-density="compact"]`, and 44px on a touch screen in either tier.
 * Anything smaller than a control grows its hit area to 44px on a coarse
 * pointer.
 */

/** Keyboard focus for anything pressable: a 2px forest outline held off the edge. */
export const focusRing =
  "outline-none focus-visible:outline-2 focus-visible:outline-solid focus-visible:outline-offset-2 focus-visible:outline-ring";

/**
 * Keyboard focus for a field: the forest edge and a soft forest ring. The
 * lime halo read loud on a field that is simply chosen (owner 2026-09-25);
 * lime stays a marketing accent, never a control state.
 */
export const fieldFocus =
  "outline-none focus-visible:border-primary focus-visible:ring-3 focus-visible:ring-primary/15";

/** A field whose value fails validation: the danger edge, focused or not. */
export const fieldInvalid =
  "aria-invalid:border-danger aria-invalid:inset-ring-[0.5px] aria-invalid:inset-ring-danger aria-invalid:focus-visible:ring-danger/20";

/** A 44px hit area on a touch screen for anything drawn smaller than a control. */
export const touchHitArea =
  "pointer-coarse:after:absolute pointer-coarse:after:top-1/2 pointer-coarse:after:left-1/2 pointer-coarse:after:size-11 pointer-coarse:after:-translate-x-1/2 pointer-coarse:after:-translate-y-1/2";

/* ── buttons ─────────────────────────────────────────────────────────────── */

const buttonBase = [
  "group/button relative inline-flex shrink-0 items-center justify-center gap-2 whitespace-nowrap rounded-pill",
  "text-(length:--nt-button-text) leading-tight font-medium select-none",
  "transition-[background-color,color,border-color,box-shadow,opacity,transform] duration-150 ease-out",
  focusRing,
  "active:not-aria-[haspopup]:scale-[0.96] disabled:pointer-events-none disabled:opacity-50",
  "aria-invalid:outline-2 aria-invalid:outline-solid aria-invalid:outline-offset-2 aria-invalid:outline-danger",
  "[&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
].join(" ");

/** The looks a button can take. Each app maps its own variant names onto these. */
const buttonLooks = {
  /** Forest pill with a light top gradient: the one main action in a view. */
  primary:
    "bg-primary bg-linear-to-b from-white/10 to-white/0 text-primary-foreground shadow-button hover:bg-primary-hover",
  /** White with the card ring, beside a primary. */
  secondary:
    "bg-card text-foreground shadow-card hover:bg-surface hover:shadow-card-hover aria-expanded:bg-background",
  /** Secondary with forest text: an action that belongs to the brand, not the page. */
  "secondary-brand":
    "bg-card text-primary shadow-card hover:bg-surface hover:text-primary-hover hover:shadow-card-hover aria-expanded:bg-background",
  /** No chrome until hovered: toolbars, row actions, close buttons. */
  ghost: "text-foreground hover:bg-muted aria-expanded:bg-muted",
  /** Forest text, for a quieter "read more". */
  link: "text-primary underline-offset-4 hover:text-primary-hover hover:underline",
  /** A destructive action that is the main one in its view. */
  danger: "bg-danger text-danger-foreground shadow-fill hover:bg-danger-hover",
  /** A destructive action beside others: tinted, not solid. */
  "danger-soft":
    "bg-danger-tint text-danger inset-ring inset-ring-danger-border hover:bg-danger-tint-hover",
  /** Secondary that turns danger on hover. */
  "danger-outline":
    "bg-card text-foreground shadow-card hover:bg-danger-tint hover:text-danger aria-expanded:bg-background",
  /** Ghost that turns danger on hover (a row's delete icon). */
  "danger-ghost":
    "text-muted-foreground hover:bg-danger-tint hover:text-danger",
  /** Danger text, no fill (an inline "Remove"). */
  "danger-text": "text-danger hover:text-danger-hover",
  /** A button that reads as a field: pickers, date and combobox triggers. */
  field:
    "justify-start rounded-control border border-input bg-card text-(length:--nt-control-text) font-normal text-foreground hover:border-muted-foreground aria-expanded:border-primary",
  /** No look at all: the children carry it. */
  bare: "",
} as const;

const buttonSizes = {
  default: "h-control px-[calc(var(--nt-control-px)+4px)]",
  xs: `h-6 gap-1 px-2.5 text-xs [&_svg:not([class*='size-'])]:size-3 ${touchHitArea}`,
  sm: `h-8 gap-1.5 px-3.5 text-sm ${touchHitArea}`,
  /** The main call to action: one step over a control, 44px, in the field text size. */
  lg: "h-11 gap-2 px-5 text-(length:--nt-control-text)",
  icon: "size-control",
  "icon-xs": `size-6 [&_svg:not([class*='size-'])]:size-3 ${touchHitArea}`,
  "icon-sm": `size-8 ${touchHitArea}`,
  "icon-lg": "size-11",
} as const;

export type ButtonLook = keyof typeof buttonLooks;
export type ButtonSize = keyof typeof buttonSizes;

/**
 * Class pieces for an app's own `buttonVariants`: build a cva from
 * `buttonRecipe.base` and map each app variant name onto `buttonRecipe.look.*`
 * and each size onto `buttonRecipe.size.*`, so the app keeps its names.
 */
export const buttonRecipe = {
  base: buttonBase,
  look: buttonLooks,
  size: buttonSizes,
} as const;

const systemButton = cva(buttonBase, {
  variants: { variant: buttonLooks, size: buttonSizes },
  defaultVariants: { variant: "primary", size: "default" },
});

/**
 * The system's own button, for new code that has no legacy names to keep.
 * Merged, so a size's `text-sm` replaces the base text size.
 */
export function buttonVariants(
  props?: Parameters<typeof systemButton>[0],
): string {
  return cn(systemButton(props));
}

/* ── fields ──────────────────────────────────────────────────────────────── */

const fieldBase = [
  "w-full min-w-0 rounded-control border border-input bg-card text-(length:--nt-control-text) text-foreground",
  "transition-[color,border-color,box-shadow] duration-150 ease-out",
  "placeholder:text-muted-foreground selection:bg-primary selection:text-primary-foreground hover:border-muted-foreground",
  fieldFocus,
  fieldInvalid,
  "disabled:pointer-events-none disabled:cursor-not-allowed disabled:bg-muted disabled:opacity-60",
].join(" ");

/**
 * The control look (company.css `.ct-field`, with a 3:1 `border-input` edge):
 * control height, forest focus edge + a soft forest ring, danger edge when invalid.
 */
export const inputVariants = cva(fieldBase, {
  variants: {
    kind: {
      input:
        "h-control px-control-x py-1 file:inline-flex file:h-7 file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground",
      textarea: "min-h-20 px-control-x py-2",
      select: "h-control appearance-none pr-9 pl-control-x",
    },
    size: {
      default: "",
      sm: "h-8 px-2.5 text-sm",
    },
    /** Identifiers (EIN, TIN, account numbers): monospace, figures aligned. */
    mono: {
      true: "font-mono tabular-nums",
      false: "",
    },
  },
  defaultVariants: { kind: "input", size: "default", mono: false },
});

/** A field label (`.ct-field label`): 14px, 500, the ink. */
export const labelRecipe =
  "flex items-center gap-2 text-sm leading-5 font-medium text-foreground select-none peer-disabled:cursor-not-allowed peer-disabled:opacity-50 group-data-[disabled=true]:pointer-events-none group-data-[disabled=true]:opacity-50";

/* ── overlays: dialog, sheet, popover, menu, select, tooltip ─────────────── */

const popIn =
  "data-open:animate-in data-open:fade-in-0 data-open:zoom-in-95 data-closed:animate-out data-closed:fade-out-0 data-closed:zoom-out-95";
const slideFromSide =
  "data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 data-[side=inline-start]:slide-in-from-right-2 data-[side=inline-end]:slide-in-from-left-2";

export const dialogRecipe = {
  /** The scrim behind a modal. */
  overlay:
    "fixed inset-0 isolate z-50 bg-scrim duration-150 data-open:animate-in data-open:fade-in-0 data-closed:animate-out data-closed:fade-out-0",
  /** A centred panel: white, 20px corners, the panel shadow. */
  content: `fixed top-1/2 left-1/2 z-50 w-full max-w-[calc(100%-2rem)] -translate-x-1/2 -translate-y-1/2 rounded-panel bg-card p-6 text-sm text-card-foreground shadow-panel outline-none duration-150 sm:max-w-md ${popIn}`,
  header: "flex flex-col gap-1.5",
  footer: "flex flex-col-reverse gap-2 sm:flex-row sm:justify-end",
  title:
    "text-(length:--nt-title) leading-(--nt-title-leading) font-semibold tracking-[-0.01em] text-foreground",
  description: "text-sm text-muted-foreground",
} as const;

export const sheetRecipe = {
  overlay: dialogRecipe.overlay,
  content:
    "fixed z-50 flex flex-col gap-4 bg-card text-sm text-card-foreground shadow-panel outline-none transition duration-200 ease-in-out data-[side=bottom]:inset-x-0 data-[side=bottom]:bottom-0 data-[side=bottom]:h-auto data-[side=bottom]:rounded-t-panel data-[side=left]:inset-y-0 data-[side=left]:left-0 data-[side=left]:h-full data-[side=left]:w-3/4 data-[side=right]:inset-y-0 data-[side=right]:right-0 data-[side=right]:h-full data-[side=right]:w-3/4 data-[side=top]:inset-x-0 data-[side=top]:top-0 data-[side=top]:h-auto data-[side=top]:rounded-b-panel sm:data-[side=left]:max-w-sm sm:data-[side=right]:max-w-sm",
  header: "flex flex-col gap-1.5 p-5",
  footer: "mt-auto flex flex-col gap-2 p-5",
  title: "text-base font-semibold text-foreground",
  description: "text-sm text-muted-foreground",
} as const;

export const popoverRecipe = {
  content: `z-50 flex w-72 origin-(--transform-origin) flex-col gap-4 rounded-menu bg-popover p-4 text-sm text-popover-foreground shadow-panel outline-none duration-100 ${popIn} ${slideFromSide}`,
  title: "font-medium text-foreground",
  description: "text-muted-foreground",
} as const;

const menuItemBase =
  "relative flex cursor-pointer items-center gap-2 rounded-[10px] px-2.5 py-2 text-sm text-foreground outline-none select-none focus:bg-accent focus:text-accent-foreground data-highlighted:bg-accent data-disabled:pointer-events-none data-disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4";

export const menuRecipe = {
  /** The floating list: white, 16px corners, the panel shadow. */
  content: `z-50 min-w-36 origin-(--transform-origin) overflow-x-hidden overflow-y-auto rounded-menu bg-popover p-1.5 text-popover-foreground shadow-panel outline-none duration-100 ${popIn} ${slideFromSide}`,
  item: menuItemBase,
  /** An item that deletes or ends something. */
  itemDanger:
    "data-[variant=destructive]:text-danger data-[variant=destructive]:focus:bg-danger-tint data-[variant=destructive]:focus:text-danger data-[variant=destructive]:*:[svg]:text-danger",
  /** Checkbox and radio items leave room for their indicator on the right. */
  checkItem: `${menuItemBase} pr-8`,
  indicator:
    "pointer-events-none absolute right-2.5 flex size-4 items-center justify-center text-primary",
  subTrigger: `${menuItemBase} data-open:bg-accent data-popup-open:bg-accent`,
  label: "px-2.5 py-1.5 text-xs font-medium text-muted-foreground",
  separator: "-mx-1.5 my-1.5 h-px bg-border",
  shortcut: "ml-auto text-xs tracking-widest text-muted-foreground",
} as const;

/** A select option: a menu row set in the field's own text size. */
const selectItemBase = menuItemBase.replace(
  "text-sm",
  "text-(length:--nt-control-text)",
);

export const selectRecipe = {
  /** Reads as a field: control height, `border-input`, the field focus. */
  trigger:
    "flex w-full items-center justify-between gap-2 whitespace-nowrap data-placeholder:text-muted-foreground *:data-[slot=select-value]:line-clamp-1 *:data-[slot=select-value]:flex *:data-[slot=select-value]:items-center *:data-[slot=select-value]:gap-2 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
  content: menuRecipe.content,
  item: `${selectItemBase} pr-8`,
  indicator: menuRecipe.indicator,
  label: menuRecipe.label,
  separator: menuRecipe.separator,
  scrollButton:
    "z-10 flex w-full cursor-default items-center justify-center bg-popover py-1 [&_svg:not([class*='size-'])]:size-4",
} as const;

export const tooltipRecipe = {
  content: `z-50 w-fit max-w-xs origin-(--transform-origin) rounded-lg bg-foreground px-3 py-1.5 text-xs text-background data-[state=delayed-open]:animate-in data-[state=delayed-open]:fade-in-0 data-[state=delayed-open]:zoom-in-95 ${popIn} ${slideFromSide}`,
  arrow: "z-50 size-2.5 rounded-[2px] bg-foreground fill-foreground",
} as const;

/* ── tabs ────────────────────────────────────────────────────────────────── */

export const tabsRecipe = {
  /** `default`: a pill track on the surface; `line`: a rule with the service underline. */
  list: {
    base: "group/tabs-list inline-flex w-fit items-center justify-center text-muted-foreground group-data-vertical/tabs:h-fit group-data-vertical/tabs:flex-col",
    default: "h-control gap-0.5 rounded-pill bg-muted p-1",
    line: "gap-5 rounded-none border-b border-border bg-transparent group-data-horizontal/tabs:h-control",
  },
  trigger: [
    "relative inline-flex h-full flex-1 items-center justify-center gap-1.5 rounded-pill px-3.5 text-(length:--nt-tab-text) font-medium whitespace-nowrap text-muted-foreground transition-[color,background-color,box-shadow] duration-150 hover:text-foreground group-data-vertical/tabs:w-full group-data-vertical/tabs:justify-start disabled:pointer-events-none disabled:opacity-50 aria-disabled:pointer-events-none aria-disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
    focusRing,
    "data-active:text-foreground group-data-[variant=default]/tabs-list:data-active:bg-card group-data-[variant=default]/tabs-list:data-active:shadow-chip",
    "group-data-[variant=line]/tabs-list:rounded-none group-data-[variant=line]/tabs-list:px-0.5",
    "after:absolute after:bg-service after:opacity-0 after:transition-opacity group-data-horizontal/tabs:after:inset-x-0 group-data-horizontal/tabs:after:-bottom-px group-data-horizontal/tabs:after:h-0.5 group-data-vertical/tabs:after:inset-y-0 group-data-vertical/tabs:after:-right-1 group-data-vertical/tabs:after:w-0.5 group-data-[variant=line]/tabs-list:data-active:after:opacity-100",
  ].join(" "),
  content: "flex-1 text-sm outline-none",
} as const;

/* ── checkbox, radio, switch ─────────────────────────────────────────────── */

const choiceBase =
  "peer relative shrink-0 border border-input bg-card outline-none transition-[background-color,border-color,box-shadow] duration-150 focus-visible:outline-2 focus-visible:outline-solid focus-visible:outline-offset-2 focus-visible:outline-ring aria-invalid:border-danger aria-invalid:ring-2 aria-invalid:ring-danger/20 disabled:cursor-not-allowed disabled:opacity-50 data-disabled:cursor-not-allowed data-disabled:opacity-50 after:absolute after:top-1/2 after:left-1/2 after:size-11 after:-translate-x-1/2 after:-translate-y-1/2";

export const checkboxRecipe = {
  root: `${choiceBase} flex size-4 items-center justify-center rounded-[5px] data-checked:border-primary data-checked:bg-primary data-checked:text-primary-foreground data-indeterminate:border-primary data-indeterminate:bg-primary data-indeterminate:text-primary-foreground`,
  indicator:
    "grid place-content-center text-current transition-none [&>svg]:size-3.5",
} as const;

export const radioRecipe = {
  root: `${choiceBase} flex aspect-square size-4 items-center justify-center rounded-full data-checked:border-primary data-checked:bg-primary`,
  indicator: "flex size-4 items-center justify-center",
  dot: "size-1.5 rounded-full bg-primary-foreground",
} as const;

export const switchRecipe = {
  root: "peer group/switch relative inline-flex shrink-0 items-center rounded-pill border border-transparent outline-none transition-[background-color,box-shadow] duration-150 focus-visible:outline-2 focus-visible:outline-solid focus-visible:outline-offset-2 focus-visible:outline-ring data-checked:bg-primary data-unchecked:bg-input aria-invalid:ring-2 aria-invalid:ring-danger/40 data-[size=default]:h-5 data-[size=default]:w-9 data-[size=sm]:h-4 data-[size=sm]:w-7 data-disabled:cursor-not-allowed data-disabled:opacity-50 after:absolute after:-inset-x-3 after:-inset-y-2",
  thumb:
    "pointer-events-none block rounded-full bg-card shadow-thumb transition-transform duration-150 group-data-[size=default]/switch:size-4 group-data-[size=sm]/switch:size-3 data-unchecked:translate-x-px group-data-[size=default]/switch:data-checked:translate-x-[17px] group-data-[size=sm]/switch:data-checked:translate-x-[13px]",
} as const;

/* ── toast ───────────────────────────────────────────────────────────────── */

export const toastRecipe = {
  root: "group pointer-events-auto relative flex w-full items-center justify-between gap-3 overflow-hidden rounded-menu p-4 pr-8 text-sm shadow-panel",
  tone: {
    default: "bg-card text-card-foreground",
    success:
      "bg-success-tint text-success inset-ring inset-ring-success-border",
    warning:
      "bg-warning-tint text-warning inset-ring inset-ring-warning-border",
    danger: "bg-danger-tint text-danger inset-ring inset-ring-danger-border",
    info: "bg-info-tint text-info inset-ring inset-ring-info-border",
  },
  title: "text-sm font-semibold",
  description: "text-sm opacity-90",
  action: `inline-flex h-8 shrink-0 items-center justify-center rounded-pill bg-card px-3.5 text-sm font-medium text-foreground shadow-card transition-colors hover:bg-background disabled:pointer-events-none disabled:opacity-50 ${focusRing}`,
  close:
    "absolute top-2 right-2 rounded-pill p-1 text-current opacity-60 transition-opacity hover:opacity-100 focus:opacity-100 focus-visible:outline-2 focus-visible:outline-solid focus-visible:outline-ring",
} as const;
